import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  CardContent,
  Card,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Box,
  Icon,
  TableContainer,
  Divider,
  CardHeader,
  useMediaQuery
} from 'investira.react.components';
import { formats, validators } from 'investira.sdk';
import { MelhorValorTableCell, PessoaImage } from '../../molecules';
import services from '../../../services';
import { calcularDiffNoPeriodo, criarHistorico } from '../ativoHistorico';
import utils from '../../../utils';

const wLabels = [
  'Ano',
  '1 Mês',
  '6 Meses',
  '12 Meses',
  '24 Meses',
  '36 Meses',
  '48 Meses',
  '60 Meses',
  'Total'
];
const wIndices = [
  { label: 'Rentabilidade', key: 'rentabilidade', criterion: 'higher' },
  {
    label: 'Rentabilidade Real',
    key: 'rentabilidadeReal',
    tooltip: 'Rentabilidade já descontada a inflação (IPCA).',
    criterion: 'higher'
  },
  { label: 'Variação', key: 'variacao', criterion: 'lower' }
];
/** Apresenta os índices de rentabilidade e carrega o IPCA do período. */
const AtivoIndicesRentabilidade = memo(pProps => {
  const {
    ativos = [],
    cotacoesPorId = {},
    headerTitle,
    sx,
    highlightEnabled = false,
    subheaderEnabled = true
  } = pProps;
  const [ipcaHistorico, setIpcaHistorico] = useState({});
  const [ipcaRequest, setIpcaRequest] = useState({ dataInicio: null, loading: false });
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));
  const xAtivos = useMemo(() => ativos.filter(xAtivo => xAtivo?.ifeed_ativo_id), [ativos]);
  const xAtivoIds = useMemo(() => xAtivos.map(xAtivo => xAtivo.ifeed_ativo_id), [xAtivos]);
  const xDados = useMemo(
    () =>
      xAtivos
        .map(xAtivo => {
          const xCotacao = cotacoesPorId[xAtivo.ifeed_ativo_id];
          if (!xCotacao) return null;
          return {
            historico: criarHistorico([...(xCotacao.data || [])]),
            loading: Boolean(xCotacao.loading),
            error: Boolean(xCotacao.error),
            nome:
              xAtivo.ticker || xAtivo.ativo_seuid || xAtivo.ativo || String(xAtivo.ifeed_ativo_id),
            raw: xAtivo
          };
        })
        .filter(Boolean),
    [cotacoesPorId, xAtivos]
  );
  const xDadosFinalizados = xDados.filter(xDado => !xDado.loading);
  const xDadosComHistorico = xDadosFinalizados.filter(
    xDado => !validators.isEmpty(xDado.historico?.full)
  );
  const xCotacoesFinalizadas =
    xDados.length === xAtivoIds.length && xDados.every(xDado => !xDado.loading);
  const xDataInicio = xDadosComHistorico.reduce((pDataInicio, pDado) => {
    const xData = pDado.historico.full[0]?.data;
    return !pDataInicio || (xData && new Date(xData) < new Date(pDataInicio)) ? xData : pDataInicio;
  }, null);
  const xIpcaLoading =
    Boolean(xDataInicio) &&
    (ipcaRequest.dataInicio !== xDataInicio || Boolean(ipcaRequest.loading));

  useEffect(() => {
    if (!xCotacoesFinalizadas || !xDataInicio) {
      setIpcaHistorico({});
      setIpcaRequest({ dataInicio: null, loading: false });
      return undefined;
    }

    let xCurrent = true;
    setIpcaRequest({ dataInicio: xDataInicio, loading: true });
    services.indicadores.cotacao(
      { indicador_id: 'IPCA', data_inicio: xDataInicio, cotacao_tipo_id: '1,4' },
      rRes => {
        if (!xCurrent) return;
        setIpcaHistorico(criarHistorico(rRes.data || []));
        setIpcaRequest({ dataInicio: xDataInicio, loading: false });
      },
      () => {
        if (!xCurrent) return;
        setIpcaHistorico({});
        setIpcaRequest({ dataInicio: xDataInicio, loading: false });
      }
    );

    return () => {
      xCurrent = false;
    };
  }, [xCotacoesFinalizadas, xDataInicio]);

  const xIpca = Object.values(ipcaHistorico).map(xHistorico =>
    calcularDiffNoPeriodo(xHistorico, 'variacao_acumulada')
  );
  const xSeries = xDadosFinalizados.map(xDado => {
    const xHistoricos = Object.values(xDado.historico);
    const xRentabilidade = xHistoricos.map(xHistorico =>
      calcularDiffNoPeriodo(xHistorico, 'cotacao_ajustada')
    );
    const xVariacao = xHistoricos.map(xHistorico =>
      calcularDiffNoPeriodo(xHistorico, 'cotacao_ajustada_ex_receitas')
    );

    return {
      ativo: xDado.nome,
      raw: xDado.raw,
      rentabilidade: xRentabilidade,
      variacao: xVariacao,
      rentabilidadeReal: xRentabilidade.map((pValor, pIndex) =>
        pValor === null || xIpca[pIndex] === null || typeof xIpca[pIndex] === 'undefined'
          ? null
          : ((pValor / 100 + 1) / (xIpca[pIndex] / 100 + 1) - 1) * 100
      )
    };
  });

  if (validators.isEmpty(xSeries)) return null;

  const pvCell = (pValue, pLoading = false) =>
    pLoading ? null : typeof pValue !== 'number' || !Number.isFinite(pValue) ? (
      <Tooltip title="Sem dado" arrow>
        <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
          <Typography
            component="span"
            variant="body2"
            color="textSecondary"
            fontWeight="bold"
            noWrap>
            -
          </Typography>
        </Box>
      </Tooltip>
    ) : (
      <Typography color={pValue < 0 && 'error.light'} variant="body2" noWrap>
        {formats.formatNumber(pValue, 2, true)} %
      </Typography>
    );

  // xIndice.key,
  // xIndice.tooltip,
  // xIndice.criterion,
  const pvRow = (pSerie, pSerieIndex, pIndice, pSeriesSize = 1) => {
    const xAtivoDescricao = utils.helpers.getAtivoDescricao(pSerie.raw);
    const xAtivoLabel = isUpSm ? xAtivoDescricao : utils.typo.shortname(xAtivoDescricao, 15);
    return (
      <TableRow>
        <TableCell
          component="th"
          scope="row"
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 1,
            backgroundColor: pTheme => pTheme.palette.secondary.dark,
            maxWidth: !isUpSm ? '200px' : 'none'
          }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" noWrap>
              {pSeriesSize > 1 ? (
                <Stack direction="row" gap={1}>
                  <Stack direction="row" gap={1}>
                    {pSerie?.raw?.logo_pessoa_id && (
                      <PessoaImage
                        logoPessoaId={pSerie.raw.logo_pessoa_id}
                        alt={pSerie.raw.pessoa}
                        hasLogo={pSerie.raw.logo_ok}
                        size={18}
                      />
                    )}
                    <Stack>{xAtivoLabel}</Stack>
                  </Stack>
                </Stack>
              ) : (
                pIndice.label
              )}
            </Typography>
            {pSeriesSize <= 1 && pIndice.tooltip && !Boolean(headerTitle) ? (
              <Tooltip title={`${pIndice.tooltip}`}>
                <Box sx={{ width: '16px', height: '16px' }}>
                  <Icon iconName="information" size={16} color="primary" />
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
        </TableCell>
        {(pSerie[pIndice.key] || []).map((xValue, xIndex) => (
          <MelhorValorTableCell
            align="right"
            isBest={
              highlightEnabled &&
              utils.comparisons
                .getBestValueIndexes(
                  xSeries.map(pOutraSerie => pOutraSerie[pIndice.key]?.[xIndex]),
                  pIndice.criterion
                )
                .includes(pSerieIndex)
            }
            key={`${pSerie.ativo}-${pIndice.key}-${xIndex}`}>
            {pvCell(xValue, pIndice.key === 'rentabilidadeReal' && xIpcaLoading)}
          </MelhorValorTableCell>
        ))}
      </TableRow>
    );
  };
  const xValuesLength = Math.max(...xSeries.map(xSerie => (xSerie.rentabilidade || []).length));

  return (
    <Stack flexGrow={1}>
      <Card sx={sx}>
        <CardHeader
          title={
            <Stack spacing={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
                  {headerTitle}
                </Typography>

                {Boolean(headerTitle) && (
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 'none'
                        }
                      }
                    }}
                    title={
                      <Stack direction="row" p={1}>
                        {Object.values(wIndices).map(pIndice => (
                          <Stack key={pIndice.label}>
                            <b>{pIndice.tooltip && `${pIndice.label}:`}</b> {pIndice.tooltip}
                          </Stack>
                        ))}
                      </Stack>
                    }
                    arrow>
                    <Box component="span">
                      <Icon iconName="information" size={16} color="primary" />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          }
          subheader={
            subheaderEnabled && (
              <Stack direction="row" gap={1} sx={{ py: 1 }}>
                {xSeries.map(xSerie => {
                  return (
                    <Chip
                      key={xSerie.ativo}
                      label={utils.helpers.getAtivoDescricao(xSerie.raw)}
                      variant="outlined"
                      color="primary"
                      size="small"
                    />
                  );
                })}
              </Stack>
            )
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <TableContainer id="table-container" sx={{ overflow: 'auto' }}>
            <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: pTheme => pTheme.palette.secondary.dark
                    }}
                  />
                  {wLabels.slice(0, xValuesLength).map(xLabel => (
                    <TableCell align="right" key={xLabel}>
                      <Typography variant="caption" color="textSecondary" noWrap>
                        {xLabel}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {wIndices.map(xIndice => {
                  return (
                    <React.Fragment key={xIndice.key}>
                      {xSeries.length > 1 && (
                        <TableRow>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              backgroundColor: pTheme => pTheme.palette.secondary.dark
                            }}>
                            <Stack direction="row" gap={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {xIndice.label}
                              </Typography>
                              {xIndice.tooltip && !Boolean(headerTitle) ? (
                                <Tooltip title={`${xIndice.tooltip}`}>
                                  <Box sx={{ width: '16px', height: '16px' }}>
                                    <Icon iconName="information" size={16} color="primary" />
                                  </Box>
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell colspan={9}></TableCell>
                        </TableRow>
                      )}
                      {xSeries.map((xSerie, xSerieIndex) => (
                        <React.Fragment key={`${xIndice.key}-${xSerie.ativo}`}>
                          {pvRow(xSerie, xSerieIndex, xIndice, xSeries.length)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoIndicesRentabilidade;
